import React, { FunctionComponent, useCallback, useState } from 'react';
import Button from 'ui/atoms/button';
import Icon from 'ui/atoms/icon';
import List from 'ui/atoms/list';
import Translate from 'ui/atoms/translate';
import useTranslate from 'ui/hooks/use-translate';
import Link from 'ui/atoms/link';
import useApiCall from 'hooks/use-api-call';
import { WalletsApi } from 'api/apis';
import { AnyServerError } from 'hooks/use-api-error';
import { useServerConfigSelector } from 'core/config/hooks';
import Checkbox from 'ui/atoms/checkbox';
import ActionButtons from 'ui/molecules/action-buttons';
import Spacer from 'ui/atoms/spacer';
import { NetworksEnum, NetworkTypeEnum } from 'src/api';

interface CreateWalletPageProps {
  invitationId: string;
  onWalletCreated?: (walletId: string) => void;
  onCancel?: () => void;
  loading?: boolean;
  error?: AnyServerError;
  networkType?: NetworkTypeEnum;
}

const CreateCashlinkWallet: FunctionComponent<CreateWalletPageProps> = ({
  onWalletCreated = () => {},
  onCancel = () => {},
  loading,
  error,
  networkType,
}) => {
  const { error: apiError, loading: loadingApi, makeAuthenticatedApi, withApi } = useApiCall();

  const [isChecked, setChecked] = useState(false);

  const walletsApi: WalletsApi = makeAuthenticatedApi(WalletsApi);

  const translate = useTranslate();

  const { config } = useServerConfigSelector();

  const onCreateCashlinkWallet = useCallback(async () => {
    await withApi(async () => {
      const { id } = await walletsApi.walletsCashlinkWalletCreate({
        cashlinkWalletCreationRequest: {
          networks: [networkType === NetworkTypeEnum.STELLAR ? NetworksEnum.STELLAR : NetworksEnum.ETHEREUM],
        },
      });

      if (!id) return;
      onWalletCreated(id);
    });
  }, [withApi, walletsApi, onWalletCreated, networkType]);

  return (
    <>
      <p>
        <Translate name="wallet.createCashlink.description" />
      </p>
      <p>
        <Translate name="wallet.createCashlink.benefitDescription" />
      </p>
      <div style={{ fontSize: '0.9rem', lineHeight: '2rem' }}>
        <List>
          <li>
            <Icon name="check" color="grayDark" />
            &nbsp;&nbsp;
            <Translate name="wallet.createCashlink.benefit1" />
          </li>
          <li>
            <Icon name="check" color="grayDark" />
            &nbsp;&nbsp;
            <Translate name="wallet.createCashlink.benefit2" />
          </li>
          <li>
            <Icon name="check" color="grayDark" />
            &nbsp;&nbsp;
            <Translate name="wallet.createCashlink.benefit3" />
          </li>
        </List>
      </div>
      <Checkbox checked={isChecked} onChange={() => setChecked(!isChecked)}>
        <span style={{ fontSize: '0.9rem' }}>
          <Translate
            name="wallet.createCashlink.tosCheckbox"
            args={[
              (txt, key) => (
                <Link href={config.cashlinkWalletTosUrl} external inline={true}>
                  {txt}
                </Link>
              ),
            ]}
          />
        </span>
      </Checkbox>
      <Button variant="link" onClick={onCancel}>
        <Translate name="wallet.otherOptions" />
      </Button>
      <ActionButtons>
        <Button
          loading={loading || loadingApi}
          fluid={true}
          variant="primary"
          onClick={onCreateCashlinkWallet}
          disabled={!isChecked}
          size="large"
        >
          <Translate name="wallet.createCashlink.createButton" />
        </Button>
      </ActionButtons>
      <Spacer y={10} />
      <Spacer y={10} />
      {error && <p className="error-text">{error.getMessage(translate)}</p>}
      {apiError && <p className="error-text">{apiError.getMessage(translate)}</p>}
    </>
  );
};

export default CreateCashlinkWallet;
